// src/utils/services/AdvancedMD/ehrNotes.js
import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const ADD_EHR_NOTE_URL = `${API_BASE_URL}/api/advancedmd/ehrnotes/add`;
const SEARCH_EHR_NOTES_URL = `${API_BASE_URL}/api/advancedmd/ehrnotes/search`;

/**
 * Inserta una nota clínica en AdvancedMD vía backend MCMCore.
 *
 * El backend toma el JSON, lo convierte en el XML que espera la PPMD-API de
 * AdvancedMD (`@action=addehrnote`), maneja la sesión/token y devuelve el
 * resultado parseado. El front NO envía Authorization ni cookies de AdvancedMD.
 *
 * @param {Object} payload - Envelope EXACTO producido por `buildEhrNotePayload(...)`.
 *
 * Ejemplo de request (POST /api/advancedmd/ehrnotes/add):
 * {
 *   "ppmdmsg": {
 *     "@action": "addehrnote",
 *     "@class": "api",
 *     "@msgtime": "05/13/2026 02:10:00 PM",
 *     "@templatename": "Annual Wellness 2.0",
 *     "@profileid": "3",
 *     "@notedatetime": "05/13/2026 02:10:00 PM",
 *     "@patientid": "129807",
 *     "@comments": "Annual Wellness 2.0",
 *     "@nocookie": "0",
 *     "pagelist": {
 *       "page": [{
 *         "@pageindex": "1",
 *         "@pagename": "Note",
 *         "fieldlist": { "field": [ ... ] }
 *       }]
 *     }
 *   }
 * }
 *
 * Respuesta exitosa (200):
 * {
 *   "id": "987654",
 *   "createdate": "05/13/2026 02:10:05 PM",
 *   "rawXml": "<ppmdresults>...</ppmdresults>"
 * }
 *
 * Posibles errores:
 * - 400: payload mal formado (campos requeridos faltantes en `ppmdmsg`).
 * - 401/403: backend no pudo autenticar contra AdvancedMD.
 * - 500: error parseando el XML de respuesta de AdvancedMD.
 *
 * @returns {Promise<{id?: string, createdate?: string, rawXml?: string}>}
 * @throws Bubblea el error de axios para que el caller muestre toast/snackbar.
 */
export async function addEhrNote(payload) {
  try {
    const res = await axios.post(ADD_EHR_NOTE_URL, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (err) {
    console.error("Error adding EHR note (backend):", err);
    throw err;
  }
}

/**
 * Consulta notas clínicas (`@action=getehrnotes`) vía backend MCMCore.
 *
 * @param {Object} payload - Body con `@action=getehrnotes` (mismo formato que Swagger).
 *
 * Ejemplo de request (POST /api/advancedmd/ehrnotes/search):
 * {
 *   "ppmdmsg": {
 *     "@action": "getehrnotes",
 *     "@class": "api",
 *     "@patientid": "129807",
 *     "@nocookie": "0"
 *   }
 * }
 *
 * Respuesta exitosa (200):
 * {
 *   "count": 12,
 *   "notes": [
 *     { "id": "987654", "templatename": "Annual Wellness 2.0", "notedatetime": "..." }
 *   ],
 *   "rawXml": "<ppmdresults>...</ppmdresults>"
 * }
 *
 * @returns {Promise<{count: number, notes: Array, rawXml: string}>}
 */
export async function searchEhrNotes(payload) {
  try {
    const res = await axios.post(SEARCH_EHR_NOTES_URL, payload, {
      headers: { "Content-Type": "application/json" },
    });
    return res.data;
  } catch (err) {
    console.error("Error searching EHR notes (backend):", err);
    throw err;
  }
}
